import { deleteDB } from 'idb'
import { clearSynapPersistedState } from '@/shared/lib/persistence'
import { usePatientStore } from '@/features/patients/model/patientStore'
import { useExamStore } from '@/features/exams/model/examStore'
import { useCalendarStore } from '@/features/calendar/model/calendarStore'
import { useClinicalDocumentStore } from '@/features/clinicalDocuments/model/clinicalDocumentStore'
import { useEncounterStore } from '@/features/encounters/model/encounterStore'
import { useAnamnesisTranscriptStore } from '@/features/encounters/anamnesis/anamnesisTranscriptStore'
import { useOutboxStore } from '@/features/offline/model/outboxStore'
import { clearMemorySyncQueue } from '@/features/local-ai/memorySyncService'
import {
  DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS,
  localVectorStore,
} from '@/features/local-ai/localVectorStore'
import { useNotificationsStore } from '@/features/notifications/model/notificationsStore'
import { usePullToRefreshLock } from '@/shared/ui/usePullToRefreshLock'
import { useSignOnSavePrefsStore } from '@/features/clinicalDocuments/model/signOnSavePrefsStore'
import { useDigitalSignaturePrefsStore } from '@/features/clinicalDocuments/model/digitalSignaturePrefsStore'

export const LOCAL_AI_IDB_NAME = 'synap-local-ai'

export const STORAGE_KEYS = [
  'synap-patient-store',
  'synap-exam-store',
  'synap-calendar-store',
  'synap-clinical-document-store',
  'synap-offline-outbox',
  'synap-local-ai-store',
  'synap-notifications',
  'synap-chat-pending-requests',
  'synap-chat-focused-patient',
  'synap-chat-focused-patient-recents',
  'synap-chat-last-view',
  'synap-chat-outbound-queue',
] as const

/** IndexedDB `deleteDB` / open pode nunca resolver quando há conexões bloqueadas. */
export const CLEAR_USER_DATA_IDB_TIMEOUT_MS = 5000

const raceWithTimeout = async <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} demorou mais que ${ms}ms`)), ms)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

const readPersistedLocalAiRetentionDays = (): number => {
  if (typeof window === 'undefined') return DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS
  try {
    const raw = window.localStorage.getItem('synap-local-ai-store')
    if (!raw) return DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS
    const parsed = JSON.parse(raw) as { state?: { memoryRetentionDays?: unknown } }
    const days = parsed?.state?.memoryRetentionDays
    if (typeof days === 'number' && Number.isFinite(days) && days >= 0) {
      return Math.floor(days)
    }
  } catch {
    /* ignore malformed persist blob */
  }
  return DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS
}

/** Boot-time TTL enforcement for local AI vectors (SEC-015 / RW-Q04). */
export const purgeLocalAiVectorStoreOnBoot = async (): Promise<void> => {
  const retentionDays = readPersistedLocalAiRetentionDays()
  if (retentionDays <= 0) return
  try {
    await raceWithTimeout(
      localVectorStore.open({ retentionDays }),
      CLEAR_USER_DATA_IDB_TIMEOUT_MS,
      'Abertura do IndexedDB local',
    )
  } catch {
    /* IDB indisponível ou timeout — não bloquear boot/login */
  }
}

export type ClearUserDataStoresOptions = {
  /**
   * RW-S01 / PT-1: durante bump de geração de re-criptografia, preservar fila offline
   * pendente — nunca descartar operações clínicas ainda não sincronizadas.
   */
  preserveOutbox?: boolean
}

/**
 * Limpa dados persistidos e estado em memória de todos os stores de dados do usuário.
 * Chamar ao trocar de usuário (login, restore com user diferente) para garantir
 * que dados do usuário anterior não sejam exibidos.
 *
 * RW-S01 (synap.storage_reencrypt_v1): quando ativo (default-on), `maybeReencryptLocalStorageOnLogin`
 * aplica bump de geração por userId antes da hidratação; logout continua limpando STORAGE_KEYS.
 *
 * Operações IndexedDB têm timeout — `deleteDB` bloqueado não pode travar login.
 */
export const clearUserDataStores = async (
  options?: ClearUserDataStoresOptions,
): Promise<void> => {
  // Antes de qualquer await: a limpeza de IDB pode continuar após o timeout do login seguinte.
  useSignOnSavePrefsStore.getState().reset()
  useDigitalSignaturePrefsStore.getState().reset()
  const preserveOutbox = options?.preserveOutbox ?? false
  const storageKeys = preserveOutbox
    ? STORAGE_KEYS.filter((key) => key !== 'synap-offline-outbox')
    : [...STORAGE_KEYS]

  useNotificationsStore.getState().clearAll()

  try {
    await raceWithTimeout(
      clearSynapPersistedState(storageKeys),
      CLEAR_USER_DATA_IDB_TIMEOUT_MS,
      'Limpeza do armazenamento local',
    )
  } catch {
    /* localStorage/IDB zustand — não bloquear login/logout */
  }

  try {
    await raceWithTimeout(
      (async () => {
        await localVectorStore.clear()
        localVectorStore.close()
        await deleteDB(LOCAL_AI_IDB_NAME)
      })(),
      CLEAR_USER_DATA_IDB_TIMEOUT_MS,
      'Limpeza do IndexedDB local',
    )
  } catch {
    /* IDB indisponível, bloqueado ou timeout — logout/login não deve travar */
    try {
      localVectorStore.close()
    } catch {
      /* ignore */
    }
  }
  try {
    sessionStorage.removeItem('synap-chat-last-view')
    sessionStorage.removeItem('synap-chat-outbound-queue')
  } catch {
    /* sessionStorage indisponível */
  }

  clearMemorySyncQueue()

  usePullToRefreshLock.setState({ lockCount: 0, locked: false })

  usePatientStore.setState({ patients: [], loaded: false })
  useExamStore.setState({ exams: [], examDictionary: [], loaded: false })
  useCalendarStore.setState({ events: [], loaded: false })
  useClinicalDocumentStore.setState({ documents: [], templates: [], loaded: false })
  useEncounterStore.setState({ encountersByPatientId: {}, loadingByPatientId: {} })
  if (!preserveOutbox) {
    useOutboxStore.setState({ items: [], isRetrying: false, lastSuccessAt: undefined })
  }
  useAnamnesisTranscriptStore.getState().resetSession()
}
